import React, { useEffect, useRef, useState, useMemo } from 'react';
import styles from './PlayerTrackingCanvas.module.scss';

/**
 * GridOccupancyCanvas renders a coarse grid occupancy visualization.
 * Robust implementation to handle varying CSV schemas and blending.
 */
const GridOccupancyCanvas = React.memo(({
  csvUrl,
  width = 500,
  height = 800,
  gridCols = 3,
  gridRows = 5,
  title,
  showLegend = true,
  rallyId,
  rallyIds,
  trackingPoints = [],
  blendWeightCsv = 0.5,
  style,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width, height });
  const [cells, setCells] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      setCanvasSize({ width, height });
      return;
    }

    const updateSize = () => {
      const nextWidth = Math.max(1, Math.round(node.clientWidth || width));
      const nextHeight = Math.max(1, Math.round(node.clientHeight || height));
      setCanvasSize((prev) => (
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);

    return () => observer.disconnect();
  }, [width, height]);

  // Normalization helper
  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // 1. CSV Loading & Parsing
  const prevParamsRef = useRef({ url: null, rid: null });

  useEffect(() => {
    const ridKey = [rallyId, rallyIds].flat().filter(Boolean).sort().join(',');
    if (prevParamsRef.current.url === csvUrl && prevParamsRef.current.rid === ridKey) return;
    prevParamsRef.current = { url: csvUrl, rid: ridKey };

    let ignore = false;
    const loadData = async () => {
      if (!csvUrl) { setStatus('ready'); setCells([]); return; }
      try {
        const res = await fetch(csvUrl);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const text = await res.text();

        // Robust CSV Parser
        const normalizedText = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = normalizedText.split('\n').filter(l => l.trim());
        if (!lines.length) { setCells([]); setStatus('ready'); return; }

        const delimiter = lines[0].includes(';') ? ';' : lines[0].includes('\t') ? '\t' : ',';
        const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase());

        const rawRows = lines.slice(1).map(line => {
          const vals = line.split(delimiter);
          const obj = {};
          headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
          return obj;
        });

        // Filter by Rally ID
        const targetRallies = new Set([rallyId, rallyIds].flat().filter(Boolean).map(String));
        let filtered = rawRows;
        if (targetRallies.size > 0) {
          filtered = rawRows.filter(r => {
            const rId = r.rallyid || r['rally id'] || r.rally || r.rallyno;
            return targetRallies.has(String(rId));
          });
        }

        // Schema Detection & Mapping
        const mappedCells = [];
        const looksLikeZone = filtered.length > 0 && (filtered[0].zone !== undefined || filtered[0].z !== undefined);
        const hasSide = filtered.length > 0 && (filtered[0].player_side || filtered[0].playerside || filtered[0].side || filtered[0].player);

        if (looksLikeZone && hasSide) {
          const sideTotals = { A: 0, B: 0 };
          const counts = new Map();

          filtered.forEach(r => {
            const z = toNumber(r.zone || r.z);
            const sideRaw = (r.player_side || r.playerside || r.side || r.player || '').toUpperCase();
            const side = sideRaw.includes('A') ? 'A' : sideRaw.includes('B') ? 'B' : sideRaw;
            const val = toNumber(r.count || r.pct || r.percent || 0);

            let localIdx = (z >= 1 && z <= 6) ? z - 1 : (z >= 7 && z <= 12) ? z - 7 : 0;
            const rIdx = (side === 'A') ? Math.floor(localIdx / 2) : 3 + Math.floor(localIdx / 2);
            const cIdx = localIdx % 2;

            const key = `${rIdx},${cIdx}`;
            const curr = counts.get(key) || { A: 0, B: 0 };
            if (side === 'A') { curr.A += val; sideTotals.A += val; }
            else { curr.B += val; sideTotals.B += val; }
            counts.set(key, curr);
          });

          counts.forEach((val, key) => {
            const [r, c] = key.split(',').map(Number);
            mappedCells.push({
              row: r, col: c,
              percentA: sideTotals.A > 0 ? (val.A / sideTotals.A) * 100 : 0,
              percentB: sideTotals.B > 0 ? (val.B / sideTotals.B) * 100 : 0
            });
          });
        } else {
          filtered.forEach(r => {
            const row = toNumber(r.row || r.r || r.i || 0);
            const col = toNumber(r.col || r.c || r.j || 0);
            const pA = toNumber(r.percenta || r.a || r.pct || 0);
            const pB = toNumber(r.percentb || r.b || 0);
            mappedCells.push({ row, col, percentA: pA, percentB: pB });
          });
        }

        if (!ignore) {
          setCells(mappedCells);
          setStatus('ready');
        }
      } catch (err) {
        console.error('[GridOccupancy] Load Error:', err);
        if (!ignore) {
          setCells([]);
          setStatus('error');
        }
      }
    };
    loadData();
    return () => { ignore = true; };
  }, [csvUrl, rallyId, rallyIds]);

  // 2. Data Blending (Memoized)
  const blendedCells = useMemo(() => {
    const finalMap = new Map();
    // Initialize all 12 zones (6 rows, 2 columns) to ensure full grid rendering
    for (let r = 0; r < 6; r++) {
      for (let c = 0; c < 2; c++) {
        finalMap.set(`${r},${c}`, { A: 0, B: 0 });
      }
    }

    cells.forEach(c => {
      finalMap.set(`${c.row},${c.col}`, { A: c.percentA, B: c.percentB });
    });

    if (Array.isArray(trackingPoints) && trackingPoints.length > 0) {
      const targetRallies = new Set([rallyId, rallyIds].flat().filter(Boolean).map(String));
      const relevantPts = trackingPoints.filter(p => !targetRallies.size || targetRallies.has(String(p.RallyID)));

      if (relevantPts.length > 0) {
        const trkCounts = new Map();
        const trkTotals = { A: 0, B: 0 };
        const L = 13.4, W = 6.1, S = 0.46;
        const midX = W / 2;
        const ptsPerSide = 3;

        relevantPts.forEach(p => {
          const side = (p.player_side || '').toUpperCase();
          const x = toNumber(p.mid_x);
          const y = toNumber(p.mid_y);
          if (x < S || x > (W - S)) return;

          const cIdx = (x < midX) ? 0 : 1;
          const sideY = (side === 'A') ? y : y - (L / 2);
          const rIdxLocal = Math.floor(Math.max(0, Math.min(2.99, (sideY / (L / 2)) * ptsPerSide)));
          const rIdx = (side === 'A') ? rIdxLocal : 3 + rIdxLocal;

          const key = `${rIdx},${cIdx}`;
          const curr = trkCounts.get(key) || { A: 0, B: 0 };
          if (side === 'A') { curr.A++; trkTotals.A++; }
          else { curr.B++; trkTotals.B++; }
          trkCounts.set(key, curr);
        });

        for (let r = 0; r < 6; r++) {
          for (let c = 0; c < 2; c++) {
            const key = `${r},${c}`;
            const csv = finalMap.get(key) || { A: 0, B: 0 };
            const trk = trkCounts.get(key) || { A: 0, B: 0 };
            const trkPctA = trkTotals.A > 0 ? (trk.A / trkTotals.A) * 100 : 0;
            const trkPctB = trkTotals.B > 0 ? (trk.B / trkTotals.B) * 100 : 0;
            const alpha = blendWeightCsv;
            finalMap.set(key, {
              A: alpha * csv.A + (1 - alpha) * trkPctA,
              B: alpha * csv.B + (1 - alpha) * trkPctB
            });
          }
        }
      }
    }

    const result = [];
    finalMap.forEach((val, key) => {
      const [r, c] = key.split(',').map(Number);
      result.push({ row: r, col: c, percentA: val.A, percentB: val.B });
    });
    return result;
  }, [cells, trackingPoints, rallyId, rallyIds, blendWeightCsv]);

  // 3. Drawing Logic
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const court = { L: 13.4, W: 6.1, S: 0.46, LW: 0.04, FS: 1.98, BS: 0.76 };
    const courtAR = court.W / court.L;
    const canvasAR = canvas.width / canvas.height;

    let scale, courtW, courtH;
    if (canvasAR > courtAR) {
      courtH = canvas.height;
      scale = courtH / court.L;
      courtW = scale * court.W;
    } else {
      courtW = canvas.width;
      scale = courtW / court.W;
      courtH = scale * court.L;
    }
    const oX = (canvas.width - courtW) / 2;
    const oY = (canvas.height - courtH) / 2;

    const drawRoundedRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    const drawCourt = () => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, Math.max(centerX, centerY) * 1.3
      );
      gradient.addColorStop(0, '#22c55e'); // Vibrant vibrant green center
      gradient.addColorStop(0.6, '#15803d'); // Transition
      gradient.addColorStop(1, '#0b4d26'); // Deeper, more "lush" dark green at edges
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.translate(oX, oY);
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = Math.max(0.8, scale * court.LW * 0.85);
      ctx.globalAlpha = 0.95;
      ctx.strokeRect(0, 0, courtW, courtH);
      const sX = scale * court.S;
      ctx.strokeRect(sX, 0, courtW - 2 * sX, courtH);
      const fsY = scale * court.FS;
      const bsY = scale * court.BS;
      const netY = courtH / 2;
      ctx.beginPath();
      ctx.moveTo(0, netY - fsY); ctx.lineTo(courtW, netY - fsY);
      ctx.moveTo(0, netY + fsY); ctx.lineTo(courtW, netY + fsY);
      ctx.moveTo(0, bsY); ctx.lineTo(courtW, bsY);
      ctx.moveTo(courtW, courtH - bsY); ctx.lineTo(0, courtH - bsY);
      ctx.moveTo(courtW / 2, 0); ctx.lineTo(courtW / 2, netY - fsY);
      ctx.moveTo(courtW / 2, netY + fsY); ctx.lineTo(courtW / 2, courtH);
      ctx.stroke();
      ctx.save();
      ctx.setLineDash([scale * 0.1, scale * 0.1]);
      ctx.beginPath(); ctx.moveTo(0, netY); ctx.lineTo(courtW, netY); ctx.stroke();
      ctx.restore();
      ctx.restore();
    };

    const drawData = () => {
      ctx.save();
      ctx.translate(oX, oY);
      const singlesW = courtW - 2 * (scale * court.S);
      const cellW = singlesW / 2;
      const sX = scale * court.S;
      const netGapM = 0.3;
      const sideLM = (court.L / 2) - (netGapM / 2);
      const rowHM = sideLM / 3;
      const getYFor = (rIdx) => (rIdx < 3) ? scale * (rIdx * rowHM) : scale * (court.L / 2 + netGapM / 2 + (rIdx - 3) * rowHM);
      const cellH = scale * rowHM;

      blendedCells.forEach(cell => {
        const useA = cell.row < 3;
        const val = useA ? cell.percentA : cell.percentB;
        // Draw even if 0 to satisfy "6 box rendering" requirement
        const x = sX + cell.col * cellW;
        const y = getYFor(cell.row);
        const hue = useA ? 0 : 210;
        const lum = 98 - (val / 100) * 60;
        // Semi-transparent to let court lines show through for premium technical look
        ctx.fillStyle = `hsla(${hue}, 85%, ${lum}%, 0.75)`;
        ctx.fillRect(x, y, cellW, cellH);
        ctx.fillStyle = 'rgba(0,0,0,0.8)';
        ctx.font = `bold ${Math.max(10, Math.floor(scale * 0.4))}px sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`${Math.round(val)}%`, x + cellW / 2, y + cellH / 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.2)';
        ctx.lineWidth = 1; ctx.strokeRect(x, y, cellW, cellH);
      });
      ctx.restore();
    };

    const drawLegend = () => {
      if (!showLegend) return;
      const lw = 60, lh = 40, pad = 8;
      const lx = canvas.width - lw - pad, ly = pad;
      ctx.save();
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.shadowBlur = 4; ctx.shadowColor = 'rgba(0,0,0,0.1)';
      drawRoundedRect(lx, ly, lw, lh, 6); ctx.fill();
      ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillStyle = 'hsl(0, 85%, 50%)'; ctx.fillRect(lx + 6, ly + 10, 10, 10);
      ctx.fillStyle = '#000'; ctx.fillText('A', lx + 20, ly + 15);
      ctx.fillStyle = 'hsl(210, 85%, 50%)'; ctx.fillRect(lx + 6, ly + 24, 10, 10);
      ctx.fillStyle = '#000'; ctx.fillText('B', lx + 20, ly + 29);
      ctx.restore();
    };

    drawCourt();
    drawData();
    drawLegend();

    if (title) {
      ctx.fillStyle = '#000'; ctx.font = 'bold 14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(title, canvas.width / 2, 18);
    }
  }, [blendedCells, canvasSize.width, canvasSize.height, showLegend, title]);

  return (
    <div ref={containerRef} style={{
      backgroundColor: '#15803d',
      borderRadius: '1rem',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255,255,255,0.1)',
      width: `${width}px`,
      height: `${height}px`,
      overflow: 'hidden',
      ...style
    }}>
      <canvas ref={canvasRef} width={canvasSize.width} height={canvasSize.height} style={{ display: 'block', width: '100%', height: '100%' }} />
    </div>
  );
});

export default GridOccupancyCanvas;
